import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import ExpertLayout from '../../components/ExpertLayout';
import { expertDashboardApi } from '../../lib/http';
import ClientFilters from '../../components/expert/clients/ClientFilters';
import ClientList from '../../components/expert/clients/ClientList';
import AddClientPanel from '../../components/expert/clients/AddClientPanel';
import CreateBookingPanel from '../../components/expert/booking/CreateBookingPanel';
import { ClientSortField, SortOrder } from '../../types/client';

const ExpertClients: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<ClientSortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [bookingClient, setBookingClient] = useState<{
    id: string;
    name: string;
    phone: string;
    email: string;
  } | null>(null);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['expert-clients', searchTerm, sortBy, sortOrder],
    queryFn: async () => {
      const params: Record<string, string> = {
        sort: sortBy,
        order: sortOrder,
      };

      if (searchTerm) {
        // Use search endpoint if there's a search term
        const response = await expertDashboardApi.expertClients.search(searchTerm);
        return { expert_clients: response.data.expert_clients };
      }

      const response = await expertDashboardApi.expertClients.list(params);
      return response.data;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  const handleAddClientSuccess = () => {
    refetch();
  };

  const handleSearch = (search: string) => {
    setSearchTerm(search);
  };

  const handleSort = (sort: ClientSortField, order: SortOrder) => {
    setSortBy(sort);
    setSortOrder(order);
  };

  const handleViewDetails = (clientId: string) => {
    navigate(`/app/clients/${clientId}`);
  };

  const handleCreateBooking = (clientId: string) => {
    const clients = data?.expert_clients || [];
    const client = clients.find((c: any) => c.id === clientId);
    if (client) {
      setBookingClient({
        id: client.id,
        name: client.effective_name || client.display_name || '',
        phone: client.effective_phone || client.custom_phone || '',
        email: client.effective_email || client.custom_email || '',
      });
    }
  };

  return (
    <ExpertLayout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Clients</h1>
            <p className="mt-2 text-sm text-gray-600">
              Manage your clients and contacts
            </p>
          </div>
          <button
            onClick={() => setIsAddPanelOpen(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add Client
          </button>
        </div>

        {/* Filters */}
        <div className="mb-6">
          <ClientFilters
            searchTerm={searchTerm}
            onSearch={handleSearch}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </div>

        {/* Error State */}
        {isError && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error loading clients</h3>
                <p className="mt-2 text-sm text-red-700">
                  {(error as any)?.message || 'Failed to load clients. Please try again.'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Clients List */}
        {!isError && (
          <ClientList
            clients={data?.expert_clients || []}
            onViewDetails={handleViewDetails}
            onCreateBooking={handleCreateBooking}
            isLoading={isLoading}
          />
        )}

        {/* Add Client Panel */}
        <AddClientPanel
          isOpen={isAddPanelOpen}
          onClose={() => setIsAddPanelOpen(false)}
          onSuccess={handleAddClientSuccess}
        />

        {/* Create Booking Panel */}
        <CreateBookingPanel
          isOpen={!!bookingClient}
          onClose={() => setBookingClient(null)}
          onSuccess={() => {
            setBookingClient(null);
            refetch();
          }}
          prefilledClient={bookingClient}
        />
      </div>
    </ExpertLayout>
  );
};

export default ExpertClients;
